// A session with the Alexa skill told as a chat (the sync page's "Ver na prática"). The lines are the skill's own
// (alexa-meu-treino: lambda/index.js, its random openers fixed to one each), filled in with the account's workouts
import { baseExerciseName } from './splitReview';

export const TALK_MOMENTS = ['start', 'pick', 'rest', 'pause', 'end'];

const suffixOf = (name) => (name || '').match(/\((warmup|warm-up|aquecimento|feeder|preparo)\)/i)?.[1]?.toLowerCase();

/**
 * Whether the account's own workouts can tell the session: the skill only asks which workout to do when there are
 * two or more, and the chat walks the first one's opening entries and its last
 */
export const canTellOwnSession = (workouts) =>
  (workouts || []).length >= 2 && (workouts[0].attributes?.exercises || []).length >= 2;

/**
 * The chat's lines, in order: { moment, who: 'you' | 'alexa' | 'note', icon?, text }.
 * `copy(key, options)` reads pages.Tutorial.talk; `letters` and `workout` ({ description, exercises }) are the
 * account's voice workouts or the example's
 */
export const buildTalk = ({ copy, firstName, letters, workout, formatNumber = String }) => {
  const entries = workout.exercises;
  const count = (value) => Math.max(1, parseInt(value, 10) || 1);
  // "uma" and "duas" for one and two, like the skill; digits after that
  const said = (value) => (value === 1 ? copy('one') : value === 2 ? copy('two') : value);
  const sets = (value) => copy('sets', { count: value, n: said(value) });
  const reps = (value) => copy('reps', { count: value, n: said(value) });
  const load = (entry) =>
    parseFloat(entry.weight) > 0 ? ` ${copy('load', { weight: formatNumber(parseFloat(entry.weight)), unit: entry.weightUnit || 'kg' })}` : '';
  // The skill reads "Supino (Aquecimento)" with a pause: "Supino, aquecimento"
  const spoken = (entry) => {
    const suffix = suffixOf(entry.name);
    return suffix ? `${baseExerciseName(entry.name)}, ${suffix}` : baseExerciseName(entry.name);
  };
  const time = (seconds) => {
    const total = Math.max(1, parseInt(seconds, 10) || 60);
    if (total < 60) return copy('seconds', { count: total });
    const minutes = copy('minutes', { count: Math.floor(total / 60) });
    return total % 60 ? copy('and', { a: minutes, b: copy('seconds', { count: total % 60 }) }) : minutes;
  };
  const list = letters.map((letter) => copy('workout', { letter }));
  const listText = list.length > 1 ? copy('and', { a: list.slice(0, -1).join(', '), b: list[list.length - 1] }) : list[0];

  const first = entries[0];
  const second = entries[1];
  const last = entries[entries.length - 1];
  // Picking up "later" lands on the next exercise, a few sets in
  const resumed = entries.find((entry) => baseExerciseName(entry.name) !== baseExerciseName(first.name)) || second;
  const description = workout.description || copy('workout', { letter: letters[0] });

  const afterFirstRest =
    count(first.series) >= 2
      ? copy('lines.nextSet', { n: 2, exercise: spoken(first), reps: reps(count(first.reps)) })
      : copy('lines.nextExercise', { exercise: spoken(second), sets: sets(count(second.series)), reps: reps(count(second.reps)), load: load(second) });

  return [
    { moment: 'start', who: 'you', text: copy('lines.start') },
    {
      moment: 'start',
      who: 'alexa',
      text: copy(firstName ? 'lines.greet' : 'lines.greetNoName', { name: firstName, count: letters.length, list: listText }),
    },
    { moment: 'pick', who: 'you', text: copy('workout', { letter: letters[0] }) },
    {
      moment: 'pick',
      who: 'alexa',
      text: copy('lines.first', { workout: description, exercise: spoken(first), sets: sets(count(first.series)), reps: reps(count(first.reps)), load: load(first) }),
    },
    { moment: 'rest', who: 'note', icon: 'set', text: copy('lines.didSet') },
    { moment: 'rest', who: 'you', text: copy('lines.rest') },
    { moment: 'rest', who: 'alexa', text: copy('lines.restReply', { time: time(first.interval) }) },
    { moment: 'rest', who: 'note', icon: 'reminder', text: copy('lines.reminder', { time: time(first.interval) }) },
    { moment: 'rest', who: 'alexa', text: afterFirstRest },
    { moment: 'pause', who: 'you', text: copy('lines.end') },
    { moment: 'pause', who: 'alexa', text: copy('lines.endReply') },
    { moment: 'pause', who: 'note', icon: 'later', text: copy('lines.later') },
    { moment: 'pause', who: 'you', text: copy('lines.resume') },
    { moment: 'pause', who: 'alexa', text: copy('lines.resumeReply', { n: Math.min(count(resumed.series), 3), exercise: spoken(resumed) }) },
    { moment: 'end', who: 'note', icon: 'reminder', text: copy('lines.lastRest') },
    { moment: 'end', who: 'alexa', text: copy('lines.lastReply', { reps: reps(count(last.reps)), exercise: spoken(last) }) },
    { moment: 'end', who: 'note', icon: 'done', text: copy('lines.logged') },
  ];
};
